'use client';

import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ScrollAnimation from '@/components/ScrollAnimation';
import { Check, X, Minus, ArrowRight, Radar, Zap, Server, Brain } from 'lucide-react';
import Link from 'next/link';
import { softwareApplicationSchema, breadcrumbSchema, faqSchema } from '@/lib/utils/structured-data';
import { SITE_URL } from '@/lib/site';

type Cell = 'yes' | 'no' | 'partial' | { text: string; strong?: boolean };

type Row = {
  capability: string;
  hint?: string;
  silker: Cell;
  waf: Cell;
  firewall: Cell;
  observability: Cell;
};

type Column = { key: keyof Omit<Row, 'capability' | 'hint'>; label: string; sub: string; highlight?: boolean };

const columns: Column[] = [
  { key: 'silker', label: 'Silker', sub: 'Runtime agent', highlight: true },
  { key: 'waf', label: 'WAF', sub: 'Cloudflare, ModSecurity' },
  { key: 'firewall', label: 'LLM Firewall', sub: 'Lakera, Prompt Security' },
  { key: 'observability', label: 'LLM Observability', sub: 'Helicone, LangSmith' },
];

const rows: Row[] = [
  {
    capability: 'Prompt injection & jailbreaks',
    hint: 'Instruction overrides, jailbreaks, extraction on LLM routes',
    silker: 'yes',
    waf: 'no',
    firewall: { text: 'Strong (ML)', strong: true },
    observability: 'no',
  },
  {
    capability: 'Outbound data-leak prevention',
    hint: 'Secrets, API keys, PII leaving your app',
    silker: 'yes',
    waf: 'partial',
    firewall: 'yes',
    observability: 'no',
  },
  {
    capability: 'OWASP Top 10 (SQLi, XSS, traversal)',
    silker: 'yes',
    waf: { text: 'Strong', strong: true },
    firewall: 'partial',
    observability: 'no',
  },
  {
    capability: 'Scanner Trap - honeypot + instant bot ban',
    hint: 'Bans scanners the moment they probe /.env, /wp-login.php, /.git',
    silker: { text: 'Unique', strong: true },
    waf: 'no',
    firewall: 'no',
    observability: 'no',
  },
  {
    capability: 'Runs in your runtime (traffic never leaves)',
    silker: 'yes',
    waf: { text: 'Edge proxy' },
    firewall: { text: 'Cloud API' },
    observability: 'yes',
  },
  {
    capability: 'Setup',
    silker: { text: 'One npm install', strong: true },
    waf: { text: 'DNS / proxy' },
    firewall: { text: 'API integration' },
    observability: { text: 'SDK' },
  },
  {
    capability: 'Self-host / Docker proxy (any stack)',
    silker: 'yes',
    waf: 'partial',
    firewall: 'no',
    observability: 'partial',
  },
  {
    capability: 'Real-time dashboard & alerts',
    silker: 'yes',
    waf: 'yes',
    firewall: 'yes',
    observability: { text: 'Strong', strong: true },
  },
  {
    capability: 'Built for SMB / indie pricing',
    silker: { text: 'Free - $39', strong: true },
    waf: { text: 'Enterprise' },
    firewall: { text: 'Enterprise' },
    observability: { text: 'Varies' },
  },
  {
    capability: 'Inline ML detection model',
    hint: 'Where heavy ML beats heuristics today',
    silker: { text: 'Heuristics + async AI verdict' },
    waf: 'no',
    firewall: { text: 'Strong (ML)', strong: true },
    observability: 'no',
  },
];

const tradeoffs = [
  {
    icon: Brain,
    title: 'You need heavy inline ML',
    body: 'If a deep, model-based classifier on every request is your hard requirement, a dedicated LLM firewall like Lakera or Prompt Security goes further than our heuristics. Silker pairs fast inline heuristics with an on-demand AI verdict layer - honest about the trade-off.',
  },
  {
    icon: Server,
    title: 'You want a global CDN / DDoS edge',
    body: 'Silker is an in-runtime agent, not an edge network. For L3/L4 DDoS, caching and a global CDN, a WAF like Cloudflare is the right layer - and Silker sits happily behind it.',
  },
  {
    icon: Zap,
    title: 'You want evals & tracing, not security',
    body: 'If your goal is prompt evals, cost tracking and LLM tracing, observability tools like Helicone or LangSmith are built for that. Silker is a security layer, not an evals platform.',
  },
];

function CellView({ value }: { value: Cell }) {
  if (value === 'yes') {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-none bg-emerald-500/10 border border-emerald-500/20">
        <Check className="w-4 h-4 text-emerald-400" />
      </span>
    );
  }
  if (value === 'no') {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-none bg-white/[0.02] border border-white/10">
        <X className="w-4 h-4 text-neutral-600" />
      </span>
    );
  }
  if (value === 'partial') {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-none bg-amber-500/10 border border-amber-500/20">
        <Minus className="w-4 h-4 text-amber-400" />
      </span>
    );
  }
  return (
    <span
      className={`text-xs leading-tight ${
        value.strong ? 'text-white font-medium' : 'text-neutral-400'
      }`}
    >
      {value.text}
    </span>
  );
}

const compareBreadcrumb = breadcrumbSchema([
  { name: 'Home', url: SITE_URL },
  { name: 'Compare', url: `${SITE_URL}/compare` },
]);

const comparesFaq = faqSchema([
  {
    question: 'What is the difference between Silker AI and a traditional WAF?',
    answer: 'A traditional WAF (like Cloudflare WAF or ModSecurity) operates at the network edge and inspects HTTP headers and payloads against static rules. Silker AI is an in-runtime security layer - it runs inside your application process or as a reverse proxy and inspects request/response context including LLM prompts, API responses and data leaks that a WAF never sees. They are complementary: run Silker behind Cloudflare for full coverage.',
  },
  {
    question: 'How does Silker AI compare to LLM firewalls like Lakera or Prompt Security?',
    answer: 'LLM firewalls like Lakera and Prompt Security focus exclusively on prompt injection detection using deep ML models on every request. Silker covers prompt injection plus the broader application layer: OWASP Top 10, response inspection (data leaks, PII, secret exposure), API schema enforcement and automated pentesting. If deep model-based classification on every prompt is your hard requirement, a dedicated LLM firewall goes further on that one axis. Silker is the broader runtime security layer.',
  },
  {
    question: 'Should I use Silker AI or Helicone / LangSmith for LLM security?',
    answer: 'Helicone and LangSmith are LLM observability and evaluation tools - they track cost, latency, prompt quality and model outputs. They are not security tools. Silker AI is a runtime security platform that blocks attacks. Use observability tools for evals and cost tracking; use Silker to block prompt injection, data leaks and application-layer attacks.',
  },
  {
    question: 'When should I use Cloudflare WAF instead of Silker AI?',
    answer: 'Choose Cloudflare WAF (or keep it in front of Silker) when you need L3/L4 DDoS mitigation, a global CDN, or IP reputation filtering at the edge before traffic reaches your origin. Silker is not a CDN or edge network - it is an in-runtime agent. The two layers complement each other: Cloudflare handles volumetric attacks and caching; Silker handles application-layer threats, prompt injection, response inspection and pentesting that Cloudflare cannot see.',
  },
  {
    question: 'Does Silker AI require code changes to deploy?',
    answer: 'No. The Docker reverse proxy option requires zero code changes - you point your domain at the Silker proxy container and it inspects traffic transparently in front of any backend (PHP, Python, Go, Java, Node.js). Alternatively, add one line with the Node SDK or a Cloudflare Worker adapter if you want in-process detection.',
  },
]);


export default function ComparePage() {
  return (
    <div className="min-h-screen bg-black">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(softwareApplicationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(compareBreadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(comparesFaq) }} />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
      </div>

      <Header />
      <ScrollAnimation />

      <main>
        <section className="relative pt-32 pb-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-16" data-scroll-animate="fade-up">
              <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
                <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
                Compare
                <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 text-white">
                Silker AI vs WAFs, LLM Firewalls and Observability Tools
              </h1>
              <p className="text-lg text-neutral-400 max-w-2xl mx-auto leading-relaxed">
                WAFs, LLM firewalls and observability tools each solve a slice of the problem.
                Here is an honest, side-by-side look at where Silker fits - and where it doesn&apos;t.
              </p>
            </div>

            {/* Comparison matrix */}
            <div className="overflow-x-auto" data-scroll-animate="fade-up">
              <table className="w-full border-collapse min-w-[720px]">
                <thead>
                  <tr>
                    <th className="text-left p-4 align-bottom w-[34%]">
                      <span className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">
                        Capability
                      </span>
                    </th>
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`p-4 text-center align-bottom ${
                          col.highlight
                            ? 'bg-rose-500/[0.07] border-t-2 border-x border-rose-500/30'
                            : ''
                        }`}
                      >
                        <div
                          className={`text-base font-bold ${
                            col.highlight ? 'text-rose-300' : 'text-white'
                          }`}
                        >
                          {col.label}
                        </div>
                        <div className="text-[11px] text-neutral-500 mt-1 font-normal">
                          {col.sub}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-t border-white/10">
                      <td className="p-4 align-top">
                        <div className="text-sm text-neutral-200 font-medium">{row.capability}</div>
                        {row.hint && (
                          <div className="text-xs text-neutral-500 mt-1 leading-snug">{row.hint}</div>
                        )}
                      </td>
                      <td className="p-4 text-center align-middle bg-rose-500/[0.07] border-x border-rose-500/30">
                        <CellView value={row.silker} />
                      </td>
                      <td className="p-4 text-center align-middle">
                        <CellView value={row.waf} />
                      </td>
                      <td className="p-4 text-center align-middle">
                        <CellView value={row.firewall} />
                      </td>
                      <td className="p-4 text-center align-middle">
                        <CellView value={row.observability} />
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td className="p-0"></td>
                    <td className="p-0 bg-rose-500/[0.07] border-x border-b-2 border-rose-500/30 h-2"></td>
                    <td colSpan={3}></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-xs text-neutral-600 mt-4 text-center">
              Competitor capabilities are our reading of publicly documented features and may change. We keep this honest -
              if we get something wrong, <Link href="/contact" className="underline hover:text-neutral-400">tell us</Link>.
            </p>
          </div>
        </section>

        {/* Scanner Trap spotlight */}
        <section className="relative py-16">
          <div className="max-w-5xl mx-auto px-6">
            <div
              className="rounded-none border border-rose-500/20 bg-rose-500/[0.04] p-8 md:p-12"
              data-scroll-animate="fade-up"
            >
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="w-14 h-14 rounded-none bg-rose-500/10 border border-rose-500/20 flex items-center justify-center shrink-0">
                  <Radar className="w-7 h-7 text-rose-400" />
                </div>
                <div>
                  <div className="inline-flex items-center gap-2 mb-3">
                    <h2 className="text-2xl font-bold text-white">Scanner Trap</h2>
                    <span className="text-[10px] uppercase tracking-wider text-rose-400 border border-rose-500/30 px-1.5 py-0.5">
                      New in v1.3.4
                    </span>
                  </div>
                  <p className="text-neutral-400 leading-relaxed mb-6 text-lg">
                    Bots probe the same paths before they attack: <span className="font-mono text-neutral-300">/.env</span>,{' '}
                    <span className="font-mono text-neutral-300">/wp-login.php</span>,{' '}
                    <span className="font-mono text-neutral-300">/.git/config</span>,{' '}
                    <span className="font-mono text-neutral-300">/phpmyadmin</span>. Your Node/Next app never serves these,
                    so a request to one is a near-certain scanner. Silker treats them as honeypots - hit one and the IP is
                    banned instantly, before it reaches a real vulnerability. Near-zero false positives, no config.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {['env-probe', 'cms-probe', 'vcs-probe', 'admin-probe', 'backup-probe'].map((c) => (
                      <span
                        key={c}
                        className="px-3 py-1 rounded-none bg-neutral-800 text-sm text-neutral-300 border border-neutral-700 font-mono"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Honest trade-offs */}
        <section className="relative py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12" data-scroll-animate="fade-up">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-none bg-amber-500/10 text-amber-400 text-xs border border-amber-500/20 mb-4">
                When Silker isn&apos;t the answer
              </div>
              <h2 className="text-3xl font-bold text-white">Honest trade-offs</h2>
              <p className="text-neutral-400 max-w-2xl mx-auto mt-4">
                No tool covers everything. Here&apos;s where another layer is the better call - and where Silker
                still sits comfortably alongside it.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10" data-scroll-animate="fade-up">
              {tradeoffs.map((t, i) => {
                const Icon = t.icon;
                return (
                  <div key={i} className="bg-neutral-950 p-8">
                    <div className="w-12 h-12 rounded-none bg-neutral-800 flex items-center justify-center mb-6">
                      <Icon className="w-6 h-6 text-neutral-300" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-3">{t.title}</h3>
                    <p className="text-neutral-400 leading-relaxed text-sm">{t.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20">
          <div className="max-w-3xl mx-auto px-6 text-center" data-scroll-animate="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">See it on your own app</h2>
            <p className="text-base text-neutral-400 mb-8">
              One npm install, free tier, no credit card. The first scanner ban usually shows up within hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                href="/#scan"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-semibold hover:bg-neutral-200 transition-colors"
              >
                Scan your app - free
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/benchmarks"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/5 text-white rounded-none text-sm font-semibold hover:bg-white/10 transition-colors border border-white/10"
              >
                See the benchmarks
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
